import { useEffect, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from './api.ts'

/**
 * The campaign the user is currently working under.
 *
 * Persisted in localStorage so it survives navigation and reloads — a consultant
 * with grants on six campaigns should not have to re-pick on every page. It is
 * only a convenience: the server re-checks the grant on every request, so a
 * stale value can never grant access, only fail to.
 *
 * One store for the whole app (2026-09-30 layout): the picker lives in the
 * header, and every page reading the selection must see a change the moment it
 * is made there. A per-component useState would leave the page on the old
 * campaign until it remounted.
 */

const STORAGE_KEY = 'blocwork.campaign'

export interface Campaign {
  campaign_id: string
  name: string
  org_name: string
  election_date: string | null
  can_build_universes: boolean
  can_run_counts: boolean
  can_view_voters: boolean
  can_manage_canvassers: boolean
  /*
   * What the CAMPAIGN bought, as distinct from what the ROLE may do (§6d).
   * Both must hold, and they fail for reasons a user acts on differently: a role
   * problem is a conversation with an admin, a closed window is a renewal.
   *
   * may_cut goes false on election day; may_report stays true for the 30-day
   * analysis window after it. So a campaign can legitimately be readable and
   * un-cuttable at the same time, and the UI has to say which rather than
   * greying a button with no explanation.
   */
  may_cut: boolean
  may_report: boolean
  cut_until: string | null
  report_until: string | null
  dialer_seats: number
  /** The most privileged role held on this campaign, as the audit trail records it. */
  role: string
  can_cut_walk_lists: boolean
  can_view_team: boolean
  /** Whether the campaign bought walk (list types include walk, or all of them). */
  walk_purchased: boolean
  /** Whether the walk app is licensed (0088) — separate from paper walk sheets. */
  walk_app: boolean
}

const listeners = new Set<() => void>()

function readStored(): string {
  // try/catch: storage can be blocked, and the app must still work without it.
  try {
    return localStorage.getItem(STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

let current = readStored()

/** Select a campaign from anywhere — the header picker, a campaign card, an admin page. */
export function selectCampaign(id: string) {
  if (id === current) return
  current = id
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Not remembered across reloads; still selected for this visit.
  }
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useCampaigns() {
  return useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiGet<{ campaigns: Campaign[] }>('/campaigns'),
  })
}

export function useSelectedCampaign() {
  const { data } = useCampaigns()
  const campaigns = data?.campaigns ?? []
  const campaignId = useSyncExternalStore(subscribe, () => current)

  useEffect(() => {
    if (campaigns.length === 0) return
    // Fall back to the first available campaign if the stored one is gone —
    // a grant can be revoked between visits.
    const valid = campaigns.some((c) => c.campaign_id === campaignId)
    if (!valid) selectCampaign(campaigns[0]!.campaign_id)
  }, [campaigns, campaignId])

  return {
    campaigns,
    campaignId,
    campaign: campaigns.find((c) => c.campaign_id === campaignId),
    select: selectCampaign,
  }
}

export function CampaignPicker({
  campaigns,
  campaignId,
  onSelect,
}: {
  campaigns: Campaign[]
  campaignId: string
  onSelect: (id: string) => void
}) {
  return (
    <label className="text-sm">
      <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
        Campaign
      </span>
      <select
        className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
        value={campaignId}
        onChange={(e) => onSelect(e.target.value)}
      >
        {campaigns.length === 0 ? <option value="">No campaigns available</option> : null}
        {campaigns.map((c) => (
          <option key={c.campaign_id} value={c.campaign_id}>
            {c.org_name} — {c.name}
          </option>
        ))}
      </select>
    </label>
  )
}
